import { z } from 'zod/v4';

/**
 * Units of measure accepted for `unit`. Mirrors the legacy order-tracing
 * `UNITS_ENUM` (v3 constants) so Order Hub lines stay interchangeable with
 * `tracing_purchase_order_lines` data.
 */
export const ORDER_LINE_UNIT_VALUES = [
  // Volume
  'LITRE',
  'MILLILITRE',
  'CENTILITRE',
  'CUBIC_DECIMETRE',
  'CUBIC_METRE',
  'GALLON',
  'QUART',
  'PINT',
  'GILL',
  'FLUID_OUNCE',
  'FLUID_DRAM',
  'MINIM',
  'BUSHEL',
  'ACRE_FOOT',
  'CORD',
  'CUBIC_YARD',
  'CUBIC_FOOT',
  'CUBIC_INCH',
  'PECK',
  // Length
  'METRE',
  'MILLIMETRE',
  'DECIMETRE',
  'CENTIMETRE',
  'INCH',
  'NAUTICAL_MILE',
  'MILE',
  'FURLONG',
  'ROD',
  'FATHOM',
  'YARD',
  'FOOT',
  // Weight or mass
  'GRAM',
  'GRAM_PER_SQUARE_METER',
  'GRAM_PER_SQUARE_FOOT',
  'GRAM_PER_SQUARE_YARD',
  'OUNCE_PER_SQUARE_YARD',
  'MILLIGRAM',
  'KILOGRAM',
  'METRIC_TON',
  'POUNDS',
  'SHORT_TON',
  'LONG_TON',
  'HUNDREDWEIGHT',
  'SHORT_HUNDREDWEIGHT',
  'LONG_HUNDREDWEIGHT',
  'OUNCE',
  'DRAM',
  'GRAIN',
  'PENNYWEIGHT',
  'SCRUPLE',
  'STONE',
  // Area
  'SQUARE_METRE',
  'SQUARE_DECIMETRE',
  'SQUARE_CENTIMETRE',
  'SQUARE_INCH',
  'SQUARE_MILE',
  'SQUARE_FEET',
  'SQUARE_ROD',
  'SQUARE_YARD',
  'ACRE',
  // Count / textile / other
  'PIECES',
  'PAIR',
  'PERCENTAGE',
  'DENIER',
  'JUTE',
  'ENGLISH_COUNT',
  'METRIC_COUNT',
  'WORSTED_COUNT',
  'TEX',
  'KILOWATT_HOUR',
  'MOUND',
] as const;

export type OrderLineUnit = (typeof ORDER_LINE_UNIT_VALUES)[number];

/**
 * The secondary measurement is always a weight in kilograms: `KILOGRAM` is the
 * only accepted `secondaryUnit`, and the service auto-fills it whenever a
 * `secondaryQuantity` is provided (see {@link OrderHubService}).
 */
export const ORDER_LINE_SECONDARY_UNIT = 'KILOGRAM' as const;

/**
 * An `order_lines` row as exposed over the API (nested under its parent order).
 * `order_lines` is scoped only by `order_id` — it has no `for_company_id` or
 * `is_removed` column; company-scoping is enforced via the parent order.
 */
export const OrderLineResourceSchema = z
  .object({
    id: z.string(),
    orderId: z.string(),
    orderLineNumber: z.string().nullable(),
    lineNo: z.number().int().nullable(),
    styleId: z.string(),
    quantity: z.number(),
    // `unit` / `secondaryUnit` are validated as enums on write (create/update)
    // but kept lenient on read so legacy/pre-enum rows never fail serialization.
    unit: z.string(),
    secondaryQuantity: z.number().nullable(),
    secondaryUnit: z.string().nullable(),
    createdAt: z.string().nullable(),
    createdByUserId: z.string().nullable(),
    createdByUserName: z.string().nullable(),
    createdByCompanyId: z.string().nullable(),
    createdByCompanyName: z.string().nullable(),
    createdByImpersonateId: z.string().nullable(),
    createdByImpersonateName: z.string().nullable(),
    createdByApiKeyId: z.string().nullable(),
    createdByApiKeyLabel: z.string().nullable(),
    updatedAt: z.string().nullable(),
    updatedByUserId: z.string().nullable(),
    updatedByUserName: z.string().nullable(),
    updatedByCompanyId: z.string().nullable(),
    updatedByCompanyName: z.string().nullable(),
    updatedByImpersonateId: z.string().nullable(),
    updatedByImpersonateName: z.string().nullable(),
    updatedByApiKeyId: z.string().nullable(),
    updatedByApiKeyLabel: z.string().nullable(),
  })
  .strict();

export type OrderLineResource = z.infer<typeof OrderLineResourceSchema>;

/**
 * `quantity`/`secondary_quantity` are `numeric(19,2)` columns (max 17 integer
 * digits). The bounds below keep over-range/negative/NaN input from overflowing
 * the column (a Postgres `22003`/`22001`-class 500) and reject business-invalid
 * quantities. `line_no` is server-assigned, so it is never accepted as input.
 */
const NUMERIC_19_2_MAX = 1e17; // exclusive upper bound for 17 integer digits

/**
 * The line fields the service/db layer needs to create a line. The parent
 * `orderId` is NOT part of this (it is a separate argument to the service);
 * the API request body is {@link CreateOrderLineBodySchema}, which adds it.
 */
export const CreateOrderLineInputSchema = z
  .object({
    orderLineNumber: z.string().min(1).max(255).optional(),
    styleId: z.string().min(1).max(36),
    quantity: z.number().finite().positive().lt(NUMERIC_19_2_MAX),
    unit: z.enum(ORDER_LINE_UNIT_VALUES),
    secondaryQuantity: z
      .number()
      .finite()
      .positive()
      .lt(NUMERIC_19_2_MAX)
      .optional(),
    secondaryUnit: z.literal(ORDER_LINE_SECONDARY_UNIT).optional(),
  })
  .strict();

export type CreateOrderLineInput = z.infer<typeof CreateOrderLineInputSchema>;

/**
 * Body accepted when updating a line — every field is optional (partial).
 * `orderLineNumber` stays unique within the order (a duplicate is a conflict),
 * and `lineNo` is server-assigned, so it is never accepted. The nullable
 * columns (`secondaryQuantity`, `secondaryUnit`) additionally accept `null`
 * so a PATCH can clear them back to `NULL`.
 */
export const UpdateOrderLineInputSchema =
  CreateOrderLineInputSchema.partial().extend({
    secondaryQuantity: z
      .number()
      .finite()
      .positive()
      .lt(NUMERIC_19_2_MAX)
      .nullable()
      .optional(),
    secondaryUnit: z.literal(ORDER_LINE_SECONDARY_UNIT).nullable().optional(),
  });

export type UpdateOrderLineInput = z.infer<typeof UpdateOrderLineInputSchema>;

/**
 * A single entry in the `lines` array of a `PATCH /orders/:id` (declarative
 * full-sync). Every field is optional, with the same nullable secondary fields
 * as {@link UpdateOrderLineInputSchema} so a patch can clear them. The optional
 * `id` disambiguates the operation:
 * - `id` present → update that existing line with the provided fields.
 * - `id` absent → create a new line; `styleId`/`quantity`/`unit` become required
 *   (enforced by the refinement below, mirroring {@link CreateOrderLineInputSchema}).
 */
export const UpsertOrderLineInputSchema = UpdateOrderLineInputSchema.extend({
  id: z.string().min(1).max(36).optional(),
}).superRefine((val, ctx) => {
  if (val.id !== undefined) return;
  for (const field of ['styleId', 'quantity', 'unit'] as const) {
    if (val[field] === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: [field],
        message: `${field} is required when creating a new order line (no id)`,
      });
    }
  }
});

export type UpsertOrderLineInput = z.infer<typeof UpsertOrderLineInputSchema>;
